/**
 * Simplified error handling using discriminated unions
 */

export type ErrorType =
  | 'AUTHENTICATION'
  | 'AUTHORIZATION'
  | 'VALIDATION'
  | 'CONFIGURATION'
  | 'API'
  | 'FILESYSTEM'
  | 'NETWORK'
  | 'DATABASE'
  | 'RATE_LIMIT'
  | 'UNKNOWN';

export interface XServeError {
  type: ErrorType;
  message: string;
  statusCode: number;
  context?: Record<string, unknown>;
  retryAfter?: number; // For rate limiting
  originalError?: Error;
}

export function createError(
  type: ErrorType,
  message: string,
  context?: Record<string, unknown>,
  originalError?: Error
): XServeError {
  const statusCodes: Record<ErrorType, number> = {
    AUTHENTICATION: 401,
    AUTHORIZATION: 403,
    VALIDATION: 400,
    CONFIGURATION: 500,
    API: 502,
    FILESYSTEM: 500,
    NETWORK: 503,
    DATABASE: 500,
    RATE_LIMIT: 429,
    UNKNOWN: 500
  };

  const base: XServeError = {
    type,
    message,
    statusCode: statusCodes[type]
  };

  if (context !== undefined) {
    (base as any).context = context;
  }
  if (originalError !== undefined) {
    (base as any).originalError = originalError;
  }

  return base;
}

// Backward compatibility exports
export const ValidationError = (message: string, context?: Record<string, unknown>): XServeError =>
  createError('VALIDATION', message, context);

// Convenience functions for common errors
export const errors = {
  authentication: (message = 'Authentication failed', context?: Record<string, unknown>): XServeError =>
    createError('AUTHENTICATION', message, context),

  authorization: (message = 'Authorization failed', context?: Record<string, unknown>): XServeError =>
    createError('AUTHORIZATION', message, context),

  validation: (message = 'Validation failed', context?: Record<string, unknown>): XServeError =>
    createError('VALIDATION', message, context),

  configuration: (message = 'Configuration error', context?: Record<string, unknown>): XServeError =>
    createError('CONFIGURATION', message, context),

  api: (message = 'API error', context?: Record<string, unknown>): XServeError =>
    createError('API', message, context),

  filesystem: (message = 'File system error', context?: Record<string, unknown>): XServeError =>
    createError('FILESYSTEM', message, context),

  network: (message = 'Network error', context?: Record<string, unknown>): XServeError =>
    createError('NETWORK', message, context),

  database: (message = 'Database error', context?: Record<string, unknown>): XServeError =>
    createError('DATABASE', message, context),

  rateLimit: (message = 'Rate limit exceeded', retryAfter?: number, context?: Record<string, unknown>): XServeError & { retryAfter?: number } => {
    const base = createError('RATE_LIMIT', message, context) as XServeError & { retryAfter?: number };
    if (retryAfter !== undefined) base.retryAfter = retryAfter;
    return base;
  },

  unknown: (message = 'Unknown error occurred', originalError?: Error, context?: Record<string, unknown>): XServeError =>
    createError('UNKNOWN', message, context, originalError)
};

/**
 * Error handler utility functions
 */
export class ErrorHandler {
  /**
   * Wraps async functions to provide consistent error handling
   */
  static async withErrorHandling<T>(
    operation: () => Promise<T>,
    context?: Record<string, unknown>
  ): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      if (this.isXServeError(error)) {
        throw error;
      }

      // Wrap unknown errors
      const wrappedError = createError(
        'UNKNOWN',
        error instanceof Error ? error.message : 'Unknown error occurred',
        { ...context, originalError: error },
        error instanceof Error ? error : undefined
      );

      throw wrappedError;
    }
  }

  /**
   * Safely executes an operation and returns a result or error
   */
  static async safeExecute<T>(
    operation: () => Promise<T>,
    context?: Record<string, unknown>
  ): Promise<{ success: true; data: T } | { success: false; error: XServeError }> {
    try {
      const data = await operation();
      return { success: true, data };
    } catch (error) {
      const xserveError = this.isXServeError(error)
        ? error
        : createError(
            'UNKNOWN',
            error instanceof Error ? error.message : 'Unknown error occurred',
            { ...context, originalError: error },
            error instanceof Error ? error : undefined
          );

      return { success: false, error: xserveError };
    }
  }

  /**
   * Checks if an error is retryable
   */
  static isRetryable(error: XServeError | Error): boolean {
    if (this.isXServeError(error)) {
      return ['NETWORK', 'API', 'RATE_LIMIT'].includes(error.type);
    }

    // Check for common retryable error messages
    const retryableMessages = [
      'timeout',
      'network',
      'connection',
      'ECONNRESET',
      'ENOTFOUND',
      'ECONNREFUSED'
    ];

    const message = error.message.toLowerCase();
    return retryableMessages.some(pattern => message.includes(pattern));
  }

  /**
   * Gets user-friendly error message
   */
  static getUserMessage(error: XServeError | Error): string {
    if (this.isXServeError(error)) {
      switch (error.type) {
        case 'AUTHENTICATION':
          return 'Authentication failed. Please check your credentials.';
        case 'AUTHORIZATION':
          return 'You do not have permission to perform this action.';
        case 'VALIDATION':
          return 'Invalid input provided. Please check your data.';
        case 'RATE_LIMIT':
          return 'Rate limit exceeded. Please try again later.';
        case 'CONFIGURATION':
          return 'Configuration error. Please check your settings.';
        case 'NETWORK':
          return 'Network error. Please check your connection.';
        default:
          return 'An unexpected error occurred. Please try again.';
      }
    }

    return 'An unexpected error occurred. Please try again.';
  }

  private static isXServeError(error: any): error is XServeError {
    return error && typeof error === 'object' && 'type' in error && 'message' in error;
  }
}

/**
 * Retry utility with exponential backoff
 */
export class RetryHandler {
  static async withRetry<T>(
    operation: () => Promise<T>,
    options: {
      maxRetries?: number;
      baseDelay?: number;
      maxDelay?: number;
      shouldRetry?: (error: XServeError | Error) => boolean;
    } = {}
  ): Promise<T> {
    const {
      maxRetries = 3,
      baseDelay = 1000,
      maxDelay = 30000,
      shouldRetry = ErrorHandler.isRetryable
    } = options;

    let lastError: XServeError | Error;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error as XServeError | Error;

        if (attempt === maxRetries || !shouldRetry(error as XServeError | Error)) {
          throw lastError;
        }

        const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw lastError!;
  }
}
